'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useRef } from 'react';

type ModalStackContextValue = {
    isModalStackOpen: () => boolean;
    pushModal: () => void;
    popModal: () => void;
};

const noop = () => {};

const defaultValue: ModalStackContextValue = {
    isModalStackOpen: () => false,
    pushModal: noop,
    popModal: noop,
};

export const ModalStackContext = createContext<ModalStackContextValue>(defaultValue);

const ModalStackProvider = ({ children }: { children: ReactNode }) => {
    const countRef = useRef(0);

    const isModalStackOpen = useCallback(() => countRef.current > 0, []);
    const pushModal = useCallback(() => { countRef.current += 1; }, []);
    const popModal = useCallback(() => { countRef.current = Math.max(0, countRef.current - 1); }, []);

    return (
        <ModalStackContext.Provider value={{ isModalStackOpen, pushModal, popModal }}>
            {children}
        </ModalStackContext.Provider>
    );
};

export const useModalStack = () => useContext(ModalStackContext);

export const useRegisterInModalStack = () => {
    const { pushModal, popModal } = useModalStack();
    useEffect(() => {
        pushModal();
        return () => popModal();
    }, [pushModal, popModal]);
};

export default ModalStackProvider;
